import type {
  NdjsonCommand,
  NdjsonCommandResult,
  NdjsonException,
  NdjsonThought,
  AgentCommand,
  AgentCommandResult,
  AgentException,
  AgentThought,
} from './AgentSession';

const isPlainNonEmptyDict = (v: unknown): v is Record<string, unknown> => {
  if (v === null || typeof v !== 'object' || Array.isArray(v)) return false;
  if (Object.getPrototypeOf(v) !== Object.prototype) return false;
  return Object.keys(v).length > 0;
};

export class AcpException implements AgentException {
  readonly type: 'exception';
  readonly message: string;
  readonly title?: string;
  readonly exceptionType?: string;

  constructor(props: NdjsonException) {
    const { exception_type, ...rest } = props;
    Object.assign(this, {
      ...rest,
      exceptionType: exception_type,
    });
    Object.freeze(this);
  }
}

export class AcpCommandResult implements AgentCommandResult {
  readonly id: string;
  readonly type = 'command_result' as const;
  readonly contentParts: string[];
  readonly rawInput: Record<string, unknown> | null;

  constructor(props: NdjsonCommandResult) {
    const rawInput = isPlainNonEmptyDict(props.raw_input)
      ? props.raw_input
      : null;
    Object.assign(this, {
      id: props.id,
      type: props.type,
      contentParts: props.content ?? [],
      rawInput,
    });
    Object.freeze(this);
  }

  get content(): string {
    return this.contentParts.join('');
  }
}

const TITLE_BLOCKLIST = new Set(['', 'UNKNOWN', '.']);

const filterTitle = (raw: string | undefined): string | null => {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (TITLE_BLOCKLIST.has(trimmed.toUpperCase())) return null;
  return trimmed;
};

export class AcpCommand implements AgentCommand {
  readonly id: string;
  readonly type = 'command' as const;
  readonly contentParts: string[];
  readonly toolName: string;
  readonly args: string[] = [];
  readonly filePaths: string[] = [];
  readonly children: AcpCommandResult[] = [];

  title: string | null;
  rawInput: Record<string, unknown> | null = null;

  constructor(props: NdjsonCommand) {
    let args;
    let filePaths;

    try {
      if (props.content?.length) {
        const parsedArgs = JSON.parse(props.content.join(''));
        args = Object.keys(parsedArgs).map(argKey => {
          if (argKey === 'file_paths') filePaths = parsedArgs[argKey];
          return `${argKey}: ${JSON.stringify(parsedArgs[argKey])}`;
        });
      }
    } catch {
      // If JSON.parse fails simply skip parsing of args, shouldn't happen in practise
    } finally {
      Object.assign(this, {
        id: props.id,
        type: props.type,
        contentParts: props.content ?? [],
        toolName: props.tool_name,
        args: args ?? [],
        filePaths: filePaths ?? [],
        title: filterTitle(props.title),
      });
      // NB: not frozen — title and rawInput are mutated by attachResult.
    }
  }

  attachResult(result: AcpCommandResult, raw: NdjsonCommandResult): void {
    this.children.push(result);
    if (this.rawInput !== null) return;
    if (!isPlainNonEmptyDict(raw.raw_input)) return;
    this.rawInput = raw.raw_input;
  }

  get content(): string {
    return this.contentParts.join('');
  }

  get rawInputArgs(): string[] {
    if (!this.rawInput) return [];
    const formatted: string[] = [];
    for (const [key, value] of Object.entries(this.rawInput)) {
      try {
        const json = JSON.stringify(value);
        if (json === undefined) continue;
        formatted.push(`${key}: ${json}`);
      } catch {
        // Skip values that can't be JSON.stringified (circular refs, BigInt, etc.).
      }
    }
    return formatted;
  }
}

type AcpThoughtChild = AcpCommand | AcpCommandResult | AcpException;

export class AcpThought implements AgentThought {
  readonly id: string;
  readonly type = 'thought' as const;
  readonly contentParts: string[] = [];
  readonly children: AcpThoughtChild[] = [];

  constructor(props: NdjsonThought) {
    Object.assign(this, {
      id: props.id,
      type: props.type,
      contentParts: props.content,
    });
    // Not frozen — children and contentParts are mutated by helper methods.
  }

  appendContent(line: NdjsonThought): void {
    this.contentParts.push(...line.content);
  }

  addCommand(line: NdjsonCommand): AcpCommand {
    const cmd = new AcpCommand(line);
    this.children.push(cmd);
    return cmd;
  }

  addOrphanResult(line: NdjsonCommandResult): void {
    this.children.push(new AcpCommandResult(line));
  }

  get content(): string {
    return this.contentParts.join('');
  }
}

export type AcpSession = AcpThought[];
